import { arrayMove } from 'react-sortable-hoc';
import { Resolution, SOCIAL_MEDIA_POST } from '../data/Resolution';
import * as Actions from './actions';
import Layer from '../data/Layer';
import { Soundtrack } from '../audio/Soundtrack';
import FlyerState, {
  getAllStorableAssets,
  getTimelineValues,
  setTimelineValues
} from './FlyerState';
import { BeatMotionSource } from '../motion/BeatMotionSource';
import PixelRatio, { PIXEL_RATIO_HIGH } from '../data/PixelRatio';
import FlyerType from '../data/FlyerType';
import { MotionSource, MotionSourceType } from '../motion/MotionSource';
import SaveState from './SaveState';
import { isStoredAsset, newUUID } from '../utils';
import { filterCache } from './Store';
import { BinaryMotionSource } from '../motion/BinaryMotionSource';
import { LinearLoopMotionSource } from '../motion/LinearLoopMotionSource';
import { FullOnMotionSource } from '../motion/FullOnMotionSource';
import { MuteMotionSource } from '../motion/MuteMotionSource';
import { MotionSourceData, TemplateUIIntroType } from '../data/FlyerData';
import { SignalSource } from '../data/SignalSource';
import { Effect } from '@beatflyer/shaders-lib';
import { CompositionEffect } from 'compose/CompositionEffect';
import { CaptionsFrame } from 'captions/CaptionsFrame';
import { CaptionsToken } from 'data/CaptionsToken';
import { AddFigmaLayersPayload } from './actions/payloads';
const createEffect = require('@beatflyer/shaders-lib').createEffect;

const initialPreviewPixelRatio = PIXEL_RATIO_HIGH;

const intialMotionSource = new BeatMotionSource();

const reducer = (
  state: FlyerState = {
    id: null,
    name: null,
    type: FlyerType.New,
    layers: [],
    motionSource: intialMotionSource,
    muteMotionSources: [
      intialMotionSource,
      new BinaryMotionSource(),
      new LinearLoopMotionSource(),
      new FullOnMotionSource()
    ],
    soundtrack: null,
    isPlaying: false,
    shouldPlay: false,
    isMuted: false,
    previewedEffect: null,
    resolution: SOCIAL_MEDIA_POST,
    previewPixelRatio: initialPreviewPixelRatio,
    hasNewChanges: false,
    saveStatus: SaveState.Saved,
    removedAssets: [],
    lastSave: 0,
    maxMutedDuration: intialMotionSource.maxDuration,
    originTemplateId: null,
    templateUIIntro: TemplateUIIntroType.None,
    captions: null,
    captionsLanguageCode: null,
    pastCommands: [],
    // presentCommand: null,
    futureCommands: []
  },
  action: any
): FlyerState => {
  switch (action.type) {
    case Actions.APP_WILL_ENTER_BACKGROUND: {
      return {
        ...state,
        isPlaying: false
      };
    }
    case Actions.TOGGLE_PLAYBACK: {
      const shouldPlay = !state.isPlaying;
      return {
        ...state,
        isPlaying: shouldPlay,
        shouldPlay
      };
    }
    case Actions.REWIND: {
      state.motionSource.jumpTo(0);
      state.motionSource.pause();
      state.layers.forEach(l => {
        l.play(0);
        l.pause();
      });
      return {
        ...state,
        isPlaying: false,
        shouldPlay: false
      };
    }
    case Actions.JUMP_PLAYBACK:
      state.motionSource.jumpTo(action.time);
      state.layers.forEach(l => {
        l.play(action.time * 0.001);
      });
      return {
        ...state,
        isPlaying: true,
        shouldPlay: true
      };

    case Actions.SHOW_EFFECT_PREVIEW: {
      const layer = action.layer as Layer;
      if (state.previewedEffect) {
        // remove existing effect
        if (state.previewedEffect instanceof CompositionEffect) {
          state.previewedEffect.reset();
          layer.previewedCompositionEffect = null;
        } else {
          const layerIndex = action.layer.effects.indexOf(
            state.previewedEffect
          );
          layer.effects.splice(layerIndex, 1);
        }
      }
      let previewedEffect: Effect | CompositionEffect;
      if (action.effectId.startsWith('composition-')) {
        const composition = CompositionEffect.createCompositionEffect(
          layer,
          action.effectId,
          action.cachedData
        );
        layer.compositionEffect?.reset();
        layer.previewedCompositionEffect = composition;
        previewedEffect = composition;
        previewedEffect.isPreviewing = true;
      } else {
        const effect = createEffect({
          ...action.cachedData,
          visible: true,
          name: action.effectId
        });
        effect.id = newUUID();
        effect.layerTransform = layer.sprite.filterTransform;
        effect.cache = filterCache;
        effect.repeat = action.layer.repeat;
        effect.pixelRatio = state.previewPixelRatio.value;
        effect.signalSource = SignalSource.Default;
        if (!action.layer.effects) {
          action.layer.effects = [];
        }
        layer.effects.push(effect);
        layer.applyEffects();
        layer.resetGeomTransformation();
        layer.needsUpdate = true;
        previewedEffect = effect;
        previewedEffect.isPreviewing = true;
      }

      return {
        ...state,
        isPlaying: true,
        shouldPlay: true,
        previewedEffect
      };
    }
    case Actions.CANCEL_EFFECT_PREVIEW: {
      if (state.previewedEffect) {
        const layer = action.layer as Layer;
        if (state.previewedEffect instanceof CompositionEffect) {
          state.previewedEffect.reset();
          layer.previewedCompositionEffect = null;
        } else {
          const layerIndex = layer.effects.indexOf(state.previewedEffect);
          layer.effects.splice(layerIndex, 1);
          layer.applyEffects();
          layer.resetGeomTransformation();
        }
      }
      return {
        ...state,
        previewedEffect: null
      };
    }

    case Actions.ADDED_FILTER: {
      return {
        ...state,
        previewedEffect: null,
        hasNewChanges: true
      };
    }

    case Actions.TOGGLE_FILTER_VISIBILITY: {
      const layer = action.layer as Layer;
      const effect = action.filter as Effect | CompositionEffect;
      effect.visible = !effect.visible;
      layer.resetGeomTransformation();
      if (!(effect instanceof CompositionEffect)) {
        layer.applyEffects();
      } else {
        effect.reset();
      }
      return {
        ...state,
        hasNewChanges: true
      };
    }

    case Actions.REMOVE_FILTER: {
      const layer = action.layer as Layer;
      const effect = action.filter as Effect | CompositionEffect;
      layer.resetGeomTransformation();
      if (effect instanceof CompositionEffect) {
        effect.reset();
        layer.compositionEffect = null;
      } else {
        layer.removeEffect(effect);
      }
      return {
        ...state,
        hasNewChanges: true
      };
    }
    case Actions.RESET_EFFECT: {
      const effect = action.effect as Effect | CompositionEffect;
      effect.restoreInitialValues();
      if (!(effect instanceof CompositionEffect)) {
        effect.signalSource = SignalSource.Default;
      }
      return {
        ...state,
        hasNewChanges: true
      };
    }

    case Actions.ADD_FRAME: {
      let isPlaying = state.isPlaying;
      let shouldPlay = state.shouldPlay;
      if (action.layer) {
        // add frame to layer
        (action.layer as Layer).addFrame(action.frame, action.index);
      } else {
        // add layer
        let wasEmpty = state.layers.length === 0;
        if (
          state.layers.length === 0 &&
          state.motionSource &&
          !state.motionSource.hasAudio
        ) {
          isPlaying = true;
          shouldPlay = true;
        }
        const layer = new Layer();
        layer.id = action.layerId ?? layer.id;
        layer.setStageSize(
          state.resolution.width * state.previewPixelRatio.value,
          state.resolution.height * state.previewPixelRatio.value
        );
        layer.addFrame(action.frame);
        state.layers.push(layer);
        if (wasEmpty && state.motionSource) {
          // restart motion source
          state.motionSource.pause();
          state.motionSource.reset();
          state.motionSource.play();
        }
      }
      return {
        ...state,
        id: state.id || undefined,
        isPlaying,
        shouldPlay,
        hasNewChanges: true
      };
    }

    case Actions.ADD_LAYERS: {
      const newLayers = action.layers as Layer[];
      const layers = [...state.layers, ...newLayers];
      return {
        ...state,
        id: state.id || undefined,
        layers,
        hasNewChanges: true
      };
    }

    case Actions.ADDED_FIGMA_LAYERS: {
      const { soundtrack, motionSource } = action as AddFigmaLayersPayload;
      if (soundtrack) {
        state.muteMotionSources.forEach(s => {
          s.soundtrackDuration = soundtrack.getDuration();
        });
      }
      let muteMotionSources = state.muteMotionSources.concat([]);
      if (
        motionSource !== undefined &&
        motionSource.type !== MotionSourceType.Soundtrack
      ) {
        // replace motion source of same type
        const index = muteMotionSources.findIndex(
          m => m.type === motionSource.type
        );
        if (index > -1) {
          muteMotionSources[index] = motionSource as MuteMotionSource;
        }
      }
      return {
        ...state,
        motionSource: motionSource ?? soundtrack ?? state.motionSource,
        muteMotionSources,
        soundtrack,
        isPlaying: true,
        shouldPlay: true
      };
    }

    case Actions.REMOVE_LAYER: {
      const layer = action.layer as Layer;
      const index = state.layers.indexOf(layer);
      state.layers.splice(index, 1); // TODO: create new array
      layer.dispose();
      return {
        ...state,
        hasNewChanges: true
      };
    }
    case Actions.RESTORE_LAYER: {
      const layer = action.layer as Layer;
      const index = action.index as number;
      const layers = [...state.layers];
      layers.splice(index, 0, layer);
      return {
        ...state,
        layers,
        hasNewChanges: true
      };
    }
    case Actions.REMOVE_FRAME:
      (action.layer as Layer).removeFrame(action.frame);
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.REPLACE_FRAME:
      (action.layer as Layer).replaceFrame(action.oldFrame, action.newFrame);
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.CHANGE_MOTION_SOURCE: {
      const oldMotionSource = state.motionSource;
      if (oldMotionSource !== state.soundtrack) {
        oldMotionSource.pause();
      }
      const motionSource = action.source as MotionSource;
      motionSource.jumpTo(0);
      if (state.soundtrack) {
        state.soundtrack.jumpTo(0);
      }
      return {
        ...state,
        motionSource,
        isPlaying: true,
        shouldPlay: true,
        hasNewChanges: true
      };
    }
    case Actions.ADD_AUDIO: {
      const soundtrack = action.soundtrack as Soundtrack;
      soundtrack.jumpTo(0);
      state.muteMotionSources.forEach(s => {
        s.soundtrackDuration = soundtrack.getDuration();
      });
      return {
        ...state,
        id: state.id || undefined,
        motionSource: soundtrack,
        soundtrack,
        captions: null,
        captionsLanguageCode: null,
        isPlaying: true,
        shouldPlay: true,
        isMuted: false,
        hasNewChanges: true
      };
    }

    case Actions.REMOVE_SOUNDTRACK: {
      state.muteMotionSources.forEach(s => {
        s.soundtrackDuration = 0;
      });
      return {
        ...state,
        hasNewChanges: true,
        motionSource:
          state.motionSource.type === MotionSourceType.Soundtrack
            ? state.muteMotionSources[0]
            : state.motionSource,
        soundtrack: null,
        captions: null,
        captionsLanguageCode: null
      };
    }

    case Actions.WILL_LOAD_EXAMPLE:
      state.layers.forEach(lay => {
        lay.dispose();
      });
      state.soundtrack?.dispose();
      return {
        ...state,
        layers: [],
        soundtrack: null,
        saveStatus: SaveState.Saved,
        removedAssets: [],
        hasNewChanges: false,
        lastSave: 0
      };

    case Actions.LOAD_EXAMPLE: {
      const soundtrack = (action.soundtrack as Soundtrack) || null;
      const motionSource: MotionSource = action.motionSource;
      if (soundtrack) {
        state.muteMotionSources.forEach(s => {
          s.soundtrackDuration = soundtrack.getDuration();
        });
      }
      let muteMotionSources = state.muteMotionSources.concat([]);
      if (motionSource.type !== MotionSourceType.Soundtrack) {
        // replace motion source of same type
        const index = muteMotionSources.findIndex(
          m => m.type === motionSource.type
        );
        if (index > -1) {
          muteMotionSources[index] = motionSource as MuteMotionSource;
        }
      }
      return {
        ...state,
        id: action.id || undefined,
        name: action.name || null,
        type: action.flyerType,
        layers: action.layers,
        motionSource,
        muteMotionSources,
        soundtrack,
        resolution: action.resolution,
        isPlaying: action.autoPlay,
        shouldPlay: action.autoPlay,
        lastSave: action.lastSave || 0,
        hasNewChanges: action.flyerType === FlyerType.New,
        originTemplateId: action.templateId || null,
        templateUIIntro: action.templateUIIntro || TemplateUIIntroType.None,
        captions: action.captions || null,
        captionsLanguageCode: action.captionsLanguageCode || null
      };
    }
    case Actions.START_RECORDING:
      if (state.previewedEffect) {
        if (state.previewedEffect instanceof CompositionEffect) {
          const layer = state.previewedEffect.layer;
          layer.previewedCompositionEffect = null;
        } else {
          state.layers.forEach(lay => {
            let previewedEffectIndex = lay.effects.indexOf(
              (state.previewedEffect as Effect)!
            );
            if (previewedEffectIndex > -1) {
              lay.effects.splice(previewedEffectIndex, 1);
            }
          });
        }
      }
      return {
        ...state,
        isPlaying: true,
        previewedEffect: null
      };

    case Actions.RECORDING_STOPPED:
      return {
        ...state,
        isPlaying: state.shouldPlay
      };

    case Actions.RECORDING_ENDED:
      return {
        ...state,
        isPlaying: false
      };

    case Actions.RESET: {
      const motionSource = new BeatMotionSource();
      state.layers.forEach(lay => {
        lay.dispose();
      });
      state.soundtrack?.dispose();
      return {
        ...state,
        id: null,
        name: null,
        type: FlyerType.New,
        layers: [],
        motionSource,
        muteMotionSources: [
          motionSource,
          new BinaryMotionSource(),
          new LinearLoopMotionSource(),
          new FullOnMotionSource()
        ],
        soundtrack: null,
        isPlaying: false,
        shouldPlay: false,
        isMuted: false,
        // isSelectingExamples: false,
        previewedEffect: null,
        hasNewChanges: false,
        saveStatus: SaveState.Saved,
        removedAssets: [],
        lastSave: 0,
        maxMutedDuration: motionSource.maxDuration,
        originTemplateId: null,
        templateUIIntro: TemplateUIIntroType.None,
        captions: null,
        captionsLanguageCode: null,
        pastCommands: [],
        futureCommands: []
      };
    }

    case Actions.CHANGE_RESOLUTION:
      const resolution = action.resolution as Resolution;
      state.layers.forEach(lay => {
        lay.setStageSize(
          resolution.width * state.previewPixelRatio.value,
          resolution.height * state.previewPixelRatio.value
        );
      });
      return {
        ...state,
        resolution,
        hasNewChanges: true
      };

    case Actions.CHANGE_PREVIEW_PIXELRATIO:
      const previewPixelRatio = action.pixelRatio as PixelRatio;
      const res = state.resolution;
      state.layers.forEach(lay => {
        lay.setStageSize(
          res.width * previewPixelRatio.value,
          res.height * previewPixelRatio.value
        );
        lay.pixelRatio = previewPixelRatio.value;
      });
      return {
        ...state,
        previewPixelRatio
      };

    case Actions.OPEN_MODAL:
    case Actions.OPEN_PAGE:
    case Actions.SHOW_MOBILE_CONTROLLER: {
      if (!state.isPlaying) {
        return state;
      }
      return {
        ...state,
        // wasPlaying: state.isPlaying,
        isPlaying: false
      };
    }

    // case Actions.CLOSE_PAGE: {
    //   return {
    //     ...state,
    //     isPlaying: state.wasPlaying,
    //     wasPlaying: false
    //   };
    // }

    case Actions.CHANGE_LAYER_REPEAT:
      action.layer.repeat = action.repeat;
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.CHANGE_LAYER_BLEND:
      action.layer.blendMode = action.blendMode;
      if (action.ignoreChange === true) {
        return state;
      }
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.CHANGE_LAYER_SYNC_PLAYBACK: {
      const layer = action.layer as Layer;
      layer.syncPlayback = !layer.syncPlayback;
      return {
        ...state,
        hasNewChanges: true
      };
    }
    case Actions.TOGGLE_LAYER_VISIBILITY: {
      const layer = action.layer as Layer;
      layer.visible = !layer.visible;
      return {
        ...state,
        hasNewChanges: true
      };
    }
    case Actions.TOGGLE_LAYER_LOCK: {
      const layer = action.layer as Layer;
      layer.locked = !layer.locked;
      return {
        ...state,
        hasNewChanges: true
      };
    }
    case Actions.SPLIT_LAYER:
    case Actions.DUPLICATE_LAYER:
      return {
        ...state,
        // isPlaying: state.wasPlaying,
        hasNewChanges: true
      };

    case Actions.SORT_LAYERS:
      state.layers = arrayMove(state.layers, action.oldIndex, action.newIndex);
      (state.layers[0] as Layer).needsUpdate = true; // force artwork update
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.SORT_FILTERS: {
      const layerToSort = action.layer as Layer;
      layerToSort.effects = arrayMove(
        layerToSort.effects,
        action.oldIndex,
        action.newIndex
      );
      layerToSort.applyEffects();
      layerToSort.needsUpdate = true; // force artwork update
      return {
        ...state,
        hasNewChanges: true
      };
    }

    case Actions.SORT_FRAMES: {
      const layerToSort = action.layer as Layer;
      layerToSort.frames = arrayMove(
        layerToSort.frames,
        action.oldIndex,
        action.newIndex
      );
      layerToSort.needsUpdate = true; // force artwork update
      return {
        ...state,
        hasNewChanges: true
      };
    }

    case Actions.RESTORE_FREE_PLAN:
      return {
        ...state,
        resolution: SOCIAL_MEDIA_POST
      };

    case Actions.WILL_SAVE_FLYER:
      return {
        ...state,
        hasNewChanges: false
      };

    case Actions.SAVE_STATE_CHANGED: {
      const saveStatus = action.state as SaveState;
      const justSaved = saveStatus === SaveState.Saved;
      return {
        ...state,
        saveStatus,
        lastSave: action.time ?? state.lastSave,
        pastCommands: justSaved ? [] : state.pastCommands,
        futureCommands: justSaved ? [] : state.futureCommands
      };
    }

    case Actions.FLYER_CREATED: {
      return {
        ...state,
        id: action.id,
        name: action.name
      };
    }

    case Actions.ASSET_UPLOADED:
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.EDITED_EFFECT: {
      if ((action.effect as Effect).isPreviewing) {
        return state;
      }
      return {
        ...state,
        hasNewChanges: true
      };
    }

    case Actions.EDITED_BOUNDING_BOX:
    case Actions.EDITED_TEXT:
    case Actions.EDITED_SOLID_COLOR:
    case Actions.EDITED_GRADIENT:
    case Actions.EDITED_SHAPE:
    case Actions.EDITED_SOUNDWAVE:
    case Actions.TEMPLATE_PROPERTY_CHANGED:
    case Actions.CONFIRM_EDIT_SHAPE:
    case Actions.MASK_CHANGED:
      return {
        ...state,
        hasNewChanges: true
      };

    case Actions.DID_REMOVE_ASSETS: {
      const removedAssets = state.removedAssets.concat([]);
      // add only assets which are not used by others
      const assetUrls = action.assets as string[];
      const storedAssetsUrls = getAllStorableAssets(state)
        .filter(asset => {
          return isStoredAsset(asset);
        })
        .map(asset => {
          return asset.url;
        });
      assetUrls.forEach(url => {
        if (storedAssetsUrls.indexOf(url) === -1) {
          removedAssets.push(url);
        }
      });
      return {
        ...state,
        removedAssets
      };
    }

    case Actions.DID_ADD_ASSETS: {
      const assetUrls = action.assets as string[];
      const removedAssets = state.removedAssets.concat([]);
      assetUrls.forEach(url => {
        const index = removedAssets.indexOf(url);
        if (index > -1) {
          removedAssets.splice(index, 1);
        }
      });
      return {
        ...state,
        removedAssets
      };
    }

    case Actions.EDIT_TIMELINE: {
      // cache layers status
      return {
        ...state,
        isPlaying: true,
        shouldPlay: true,
        lastTimelinesValues: getTimelineValues(state)
      };
    }

    case Actions.CANCEL_EDIT_TIMELINE: {
      // restore layers cache
      if (state.lastTimelinesValues) {
        setTimelineValues(state, state.lastTimelinesValues);
      }
      return {
        ...state,
        lastTimelinesValues: undefined
      };
    }

    case Actions.SAVE_EDIT_TIMELINE: {
      const newTimeline = action.data as string;
      const hasChanged = state.lastTimelinesValues !== newTimeline;
      if (hasChanged) {
        // remove custom timelines which are default
        state.layers.forEach(layer => {
          layer.cleanTimelines();
        });
        setTimelineValues(state, newTimeline);
      }
      return {
        ...state,
        hasNewChanges: state.hasNewChanges || hasChanged,
        lastTimelinesValues: undefined
      };
    }

    case Actions.EDIT_MOTION_SOURCE: {
      const mutedMotionSource = state.motionSource as MuteMotionSource;
      return {
        ...state,
        isPlaying: true,
        shouldPlay: true,
        lastTimeMotionSourceValues: JSON.stringify(mutedMotionSource.asData())
      };
    }

    case Actions.CANCEL_EDIT_MOTION_SOURCE: {
      if (state.lastTimeMotionSourceValues) {
        const mutedMotionSource = state.motionSource as MuteMotionSource;
        const lastData = JSON.parse(
          state.lastTimeMotionSourceValues
        ) as MotionSourceData;
        mutedMotionSource.populateWithData(lastData);
      }
      return {
        ...state,
        lastTimeMotionSourceValues: undefined
      };
    }

    case Actions.SAVE_EDIT_MOTION_SOURCE: {
      const newMotion = action.data;
      const hasChanged = state.lastTimeMotionSourceValues !== newMotion;
      if (hasChanged) {
        const mutedMotionSource = state.motionSource as MuteMotionSource;
        const data = JSON.parse(action.data);
        mutedMotionSource.populateWithData(data);
      }
      return {
        ...state,
        hasNewChanges: state.hasNewChanges || hasChanged,
        lastTimeMotionSourceValues: undefined
      };
    }

    case Actions.SET_MAX_MUTED_DURATION: {
      const maxMutedDuration = action.duration as number;
      state.muteMotionSources.forEach(s => {
        s.maxDuration = maxMutedDuration;
      });
      return {
        ...state,
        maxMutedDuration,
        hasNewChanges: true
      };
    }

    case Actions.SET_MUTED_BPM: {
      state.muteMotionSources.forEach(ms => {
        ms.bpm = action.bpm;
      });
      state.motionSource.resume();
      if (state.soundtrack) {
        state.soundtrack.resume();
      }
      return {
        ...state,
        isPlaying: true,
        shouldPlay: true,
        hasNewChanges: true
      };
    }

    case Actions.CREATE_EMPTY_FLYER:
    case Actions.IMPORT_DESIGN: {
      return {
        ...state,
        id: undefined
      };
    }

    case Actions.TOGGLE_AUDIO_MUTE: {
      return {
        ...state,
        isMuted: !state.isMuted
      };
    }

    case Actions.TEMPLATE_INTRO_CHANGED: {
      return {
        ...state,
        templateUIIntro: action.intro,
        hasNewChanges: true
      };
    }

    case Actions.SAVE_CAPTIONS: {
      const captions: CaptionsToken[] | null = action.captions;
      const captionsLanguageCode = action.languageCode as string;
      state.layers.forEach(layer => {
        layer.frames.forEach(frame => {
          if (frame instanceof CaptionsFrame) {
            frame.tokens = captions ?? [];
          }
        });
      });
      return {
        ...state,
        captions,
        captionsLanguageCode,
        hasNewChanges: true
      };
    }

    case Actions.DID_RENAME_PROJECT: {
      const changedFlyerId = action.id;
      const newName = action.name;
      return {
        ...state,
        name: changedFlyerId === state.id ? newName : state.name
      };
    }

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/state/FlyerReducer.ts